import type { WhiteboardObject } from "@/api/connectApi";

/**
 * The meeting whiteboard's data model, geometry and rendering — plain
 * <canvas> 2D, no drawing library. World coordinates are unbounded; the view
 * maps them to the screen (`screen = (world - view.xy) * view.zoom`).
 */

export type WbObject = WhiteboardObject;
export type WbKind = WhiteboardObject["kind"];

export interface WbOp {
    op: "add" | "update" | "delete";
    obj?: WbObject;
    ids?: string[];
}

/** World coordinates of the canvas's top-left corner, and the scale. */
export interface View {
    x: number;
    y: number;
    zoom: number;
}

export interface Rect {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export const INK = "#1f2937";
export const PEN_COLORS: ReadonlyArray<{ value: string; label: string }> = [
    { value: INK, label: "Black" },
    { value: "#dc2626", label: "Red" },
    { value: "#ea580c", label: "Orange" },
    { value: "#ca8a04", label: "Yellow" },
    { value: "#16a34a", label: "Green" },
    { value: "#0078d4", label: "Blue" },
    { value: "#7c3aed", label: "Purple" },
    { value: "#db2777", label: "Pink" }
];
export const HIGHLIGHT_COLORS: ReadonlyArray<{ value: string; label: string }> = [
    { value: "#facc15", label: "Yellow" },
    { value: "#4ade80", label: "Green" },
    { value: "#60a5fa", label: "Blue" },
    { value: "#f472b6", label: "Pink" },
    { value: "#fb923c", label: "Orange" }
];
export const NOTE_COLORS: ReadonlyArray<{ value: string; label: string }> = [
    { value: "#fef08a", label: "Yellow" },
    { value: "#bbf7d0", label: "Green" },
    { value: "#bfdbfe", label: "Blue" },
    { value: "#fbcfe8", label: "Pink" },
    { value: "#fed7aa", label: "Orange" }
];

export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8;
export const NOTE_W = 220;
export const NOTE_H = 160;
export const LINE_HEIGHT = 1.3;
/** Client cap for one stroke (the server allows 4000 numbers). */
export const MAX_STROKE_NUMBERS = 2400;
/** Live preview of a stroke is thinned to this many numbers. */
export const PREVIEW_STROKE_NUMBERS = 400;
/** Budget for one WhiteboardOps call (the server refuses > 30 000 chars). */
export const MAX_OPS_JSON = 20_000;

export function newObjectId(): string {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === "function") return c.randomUUID().replace(/-/g, "");
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/** Drop the nulls the hub sends for fields a kind doesn't use. */
export function normalizeObject(o: WbObject): WbObject {
    const out: WbObject = { id: o.id, kind: o.kind, color: o.color, size: o.size };
    if (Array.isArray(o.points)) out.points = o.points;
    if (typeof o.x === "number") out.x = o.x;
    if (typeof o.y === "number") out.y = o.y;
    if (typeof o.w === "number") out.w = o.w;
    if (typeof o.h === "number") out.h = o.h;
    if (typeof o.text === "string") out.text = o.text;
    if (o.author) out.author = o.author;
    if (o.authorName) out.authorName = o.authorName;
    return out;
}

/** What goes over the wire: no author fields (the server stamps those), coordinates to 0.1. */
export function wireObject(o: WbObject): WbObject {
    const r = (v: number) => Math.round(v * 10) / 10;
    const out: WbObject = { id: o.id, kind: o.kind, color: o.color, size: r(o.size) };
    if (o.points) out.points = o.points.map(r);
    if (typeof o.x === "number") out.x = r(o.x);
    if (typeof o.y === "number") out.y = r(o.y);
    if (typeof o.w === "number") out.w = r(o.w);
    if (typeof o.h === "number") out.h = r(o.h);
    if (typeof o.text === "string") out.text = o.text;
    return out;
}

// ── Store ───────────────────────────────────────────────────────────────────

/**
 * The board as this client knows it, plus other people's in-progress strokes.
 * Lives outside React state: strokes arrive far too often for re-renders, so
 * the canvas subscribes and repaints on its own.
 */
export class BoardStore {
    private order: string[] = [];
    private byId = new Map<string, WbObject>();
    private cache: WbObject[] | null = null;
    private listeners = new Set<() => void>();
    /** connectionId → the stroke/shape they're drawing right now. */
    readonly previews = new Map<string, { name: string; obj: WbObject; at: number }>();
    version = 0;

    load(objects: WbObject[]) {
        this.order = [];
        this.byId.clear();
        for (const raw of objects) {
            const o = normalizeObject(raw);
            if (!this.byId.has(o.id)) this.order.push(o.id);
            this.byId.set(o.id, o);
        }
        this.previews.clear();
        this.emit();
    }

    get(id: string): WbObject | undefined {
        return this.byId.get(id);
    }

    list(): WbObject[] {
        if (!this.cache) this.cache = this.order.map((id) => this.byId.get(id)).filter((o): o is WbObject => Boolean(o));
        return this.cache;
    }

    get size(): number {
        return this.order.length;
    }

    apply(ops: WbOp[]) {
        let changed = false;
        for (const op of ops) {
            if ((op.op === "add" || op.op === "update") && op.obj) {
                const o = normalizeObject(op.obj);
                // An update never brings back something already erased (the server refuses it too).
                if (op.op === "update" && !this.byId.has(o.id)) continue;
                if (!this.byId.has(o.id)) this.order.push(o.id);
                this.byId.set(o.id, o);
                changed = true;
            } else if (op.op === "delete" && op.ids && op.ids.length > 0) {
                const gone = new Set(op.ids.filter((id) => this.byId.delete(id)));
                if (gone.size > 0) {
                    this.order = this.order.filter((id) => !gone.has(id));
                    changed = true;
                }
            }
        }
        if (changed) this.emit();
    }

    setPreview(connectionId: string, name: string, obj: WbObject | null) {
        if (obj) this.previews.set(connectionId, { name, obj: normalizeObject(obj), at: Date.now() });
        else if (!this.previews.delete(connectionId)) return;
        this.emit(false);
    }

    /** Previews from someone who went quiet (dropped mid-stroke) fade out. */
    prunePreviews(maxAgeMs: number) {
        const cutoff = Date.now() - maxAgeMs;
        let changed = false;
        for (const [k, v] of this.previews) {
            if (v.at < cutoff) {
                this.previews.delete(k);
                changed = true;
            }
        }
        if (changed) this.emit(false);
    }

    subscribe(fn: () => void): () => void {
        this.listeners.add(fn);
        return () => { this.listeners.delete(fn); };
    }

    private emit(content = true) {
        if (content) {
            this.version++;
            this.cache = null;
        }
        for (const l of Array.from(this.listeners)) l();
    }
}

// ── Geometry ───────────────────────────────────────────────────────────────

const FONT_STACK = `ui-sans-serif, system-ui, -apple-system, Roboto, sans-serif`;
export const fontFor = (size: number) => `${size}px ${FONT_STACK}`;

let measureCtx: CanvasRenderingContext2D | null | undefined;
function measurer(): CanvasRenderingContext2D | null {
    if (measureCtx === undefined) {
        try { measureCtx = document.createElement("canvas").getContext("2d"); } catch { measureCtx = null; }
    }
    return measureCtx;
}

/** Lines of a text object, word-wrapped to `maxWidth` when given. */
export function textLines(text: string, size: number, maxWidth?: number | null): string[] {
    const paragraphs = text.split("\n");
    const ctx = measurer();
    if (!ctx || !maxWidth || maxWidth <= 0) return paragraphs;
    ctx.font = fontFor(size);
    const out: string[] = [];
    for (const para of paragraphs) {
        const words = para.split(/(\s+)/);
        let line = "";
        for (const word of words) {
            const candidate = line + word;
            if (ctx.measureText(candidate).width <= maxWidth || line.length === 0) {
                // A single word wider than the box is broken by characters.
                if (line.length === 0 && ctx.measureText(word).width > maxWidth) {
                    let chunk = "";
                    for (const ch of word) {
                        if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
                            out.push(chunk);
                            chunk = ch;
                        } else chunk += ch;
                    }
                    line = chunk;
                } else line = candidate;
            } else {
                out.push(line.trimEnd());
                line = word.trimStart();
            }
        }
        out.push(line);
    }
    return out;
}

function textSize(o: WbObject): { w: number; h: number } {
    const lines = textLines(o.text ?? "", o.size, o.w);
    const ctx = measurer();
    let w = o.w ?? 0;
    if (!o.w) {
        if (ctx) {
            ctx.font = fontFor(o.size);
            for (const l of lines) w = Math.max(w, ctx.measureText(l).width);
        } else {
            w = Math.max(...lines.map((l) => l.length)) * o.size * 0.6;
        }
    }
    return { w: Math.max(w, o.size), h: Math.max(1, lines.length) * o.size * LINE_HEIGHT };
}

export function bounds(o: WbObject): Rect {
    const pad = o.size / 2;
    switch (o.kind) {
        case "text": {
            const s = textSize(o);
            const x = o.x ?? 0;
            const y = o.y ?? 0;
            return { x1: x, y1: y, x2: x + s.w, y2: y + s.h };
        }
        case "note": {
            const x = o.x ?? 0;
            const y = o.y ?? 0;
            return { x1: x, y1: y, x2: x + (o.w ?? NOTE_W), y2: y + (o.h ?? NOTE_H) };
        }
        default: {
            const p = o.points ?? [];
            if (p.length < 2) return { x1: 0, y1: 0, x2: 0, y2: 0 };
            let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
            for (let i = 0; i + 1 < p.length; i += 2) {
                const x = p[i]!, y = p[i + 1]!;
                if (x < x1) x1 = x;
                if (x > x2) x2 = x;
                if (y < y1) y1 = y;
                if (y > y2) y2 = y;
            }
            const extra = o.kind === "arrow" ? arrowHead(o.size) : 0;
            return { x1: x1 - pad - extra, y1: y1 - pad - extra, x2: x2 + pad + extra, y2: y2 + pad + extra };
        }
    }
}

export function intersects(a: Rect, b: Rect): boolean {
    return a.x1 <= b.x2 && a.x2 >= b.x1 && a.y1 <= b.y2 && a.y2 >= b.y1;
}

export function normRect(x1: number, y1: number, x2: number, y2: number): Rect {
    return { x1: Math.min(x1, x2), y1: Math.min(y1, y2), x2: Math.max(x1, x2), y2: Math.max(y1, y2) };
}

export function contentBounds(objects: WbObject[]): Rect | null {
    if (objects.length === 0) return null;
    let r: Rect | null = null;
    for (const o of objects) {
        const b = bounds(o);
        r = r ? { x1: Math.min(r.x1, b.x1), y1: Math.min(r.y1, b.y1), x2: Math.max(r.x2, b.x2), y2: Math.max(r.y2, b.y2) } : b;
    }
    return r;
}

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const dx = bx - ax, dy = by - ay;
    const len = dx * dx + dy * dy;
    let t = len === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    return Math.hypot(px - cx, py - cy);
}

/** Is world point (px, py) on the object (within `tol` world units)? */
export function hitTest(o: WbObject, px: number, py: number, tol: number): boolean {
    const b = bounds(o);
    if (px < b.x1 - tol || px > b.x2 + tol || py < b.y1 - tol || py > b.y2 + tol) return false;
    switch (o.kind) {
        case "pen":
        case "highlighter":
        case "line":
        case "arrow": {
            const p = o.points ?? [];
            const reach = o.size / 2 + tol;
            if (p.length === 2) return Math.hypot(px - p[0]!, py - p[1]!) <= reach;
            for (let i = 0; i + 3 < p.length; i += 2) {
                if (distToSegment(px, py, p[i]!, p[i + 1]!, p[i + 2]!, p[i + 3]!) <= reach) return true;
            }
            return false;
        }
        default:
            // Shapes, text and notes: anywhere in the box.
            return true;
    }
}

/** Topmost object under the point. */
export function topHit(objects: WbObject[], px: number, py: number, tol: number): WbObject | null {
    for (let i = objects.length - 1; i >= 0; i--) {
        if (hitTest(objects[i]!, px, py, tol)) return objects[i]!;
    }
    return null;
}

export function moveObject(o: WbObject, dx: number, dy: number): WbObject {
    const out = { ...o };
    if (o.points) out.points = o.points.map((v, i) => v + (i % 2 === 0 ? dx : dy));
    if (typeof o.x === "number") out.x = o.x + dx;
    if (typeof o.y === "number") out.y = o.y + dy;
    return out;
}

/** Evenly thin a stroke to at most `maxNumbers` numbers, keeping its ends. */
export function thinStroke(points: number[], maxNumbers: number): number[] {
    if (points.length <= maxNumbers) return points;
    const pairs = points.length / 2;
    const keep = Math.max(2, Math.floor(maxNumbers / 2));
    const step = (pairs - 1) / (keep - 1);
    const out: number[] = [];
    for (let i = 0; i < keep; i++) {
        const idx = Math.min(pairs - 1, Math.round(i * step));
        out.push(points[idx * 2]!, points[idx * 2 + 1]!);
    }
    return out;
}

export function clampZoom(z: number): number {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** A view that shows `r` inside a `w`×`h` viewport with some margin. */
export function fitView(r: Rect | null, w: number, h: number): View {
    if (!r || w <= 0 || h <= 0) return { x: -w / 2, y: -h / 2, zoom: 1 };
    const pad = 48;
    const bw = Math.max(1, r.x2 - r.x1), bh = Math.max(1, r.y2 - r.y1);
    const zoom = clampZoom(Math.min(2, (w - pad * 2) / bw, (h - pad * 2) / bh));
    const cx = (r.x1 + r.x2) / 2, cy = (r.y1 + r.y2) / 2;
    return { x: cx - w / 2 / zoom, y: cy - h / 2 / zoom, zoom };
}

// ── Painting ───────────────────────────────────────────────────────────────

export const BOARD_BG = { light: "#ffffff", dark: "#18181b" } as const;
const GRID = { light: "#e4e4e7", dark: "#2c2c31" } as const;
const DARK_INK = "#f4f4f5";

/** Black ink turns light on the dark board (and back in exports), so nothing vanishes. */
export function inkFor(color: string, dark: boolean): string {
    return dark && color.toLowerCase() === INK ? DARK_INK : color;
}

function arrowHead(size: number): number {
    return Math.max(10, size * 3);
}

export function drawObject(ctx: CanvasRenderingContext2D, o: WbObject, dark: boolean, alpha = 1) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const p = o.points ?? [];
    switch (o.kind) {
        case "pen":
        case "highlighter": {
            if (p.length < 2) break;
            const colour = inkFor(o.color, dark);
            if (o.kind === "highlighter") {
                ctx.globalAlpha = alpha * 0.38;
                ctx.globalCompositeOperation = dark ? "screen" : "multiply";
            }
            if (p.length === 2) {
                ctx.fillStyle = colour;
                ctx.beginPath();
                ctx.arc(p[0]!, p[1]!, o.size / 2, 0, Math.PI * 2);
                ctx.fill();
                break;
            }
            ctx.strokeStyle = colour;
            ctx.lineWidth = o.size;
            ctx.beginPath();
            ctx.moveTo(p[0]!, p[1]!);
            // Quadratic smoothing through the midpoints.
            for (let i = 2; i + 3 < p.length; i += 2) {
                const mx = (p[i]! + p[i + 2]!) / 2, my = (p[i + 1]! + p[i + 3]!) / 2;
                ctx.quadraticCurveTo(p[i]!, p[i + 1]!, mx, my);
            }
            ctx.lineTo(p[p.length - 2]!, p[p.length - 1]!);
            ctx.stroke();
            break;
        }
        case "line":
        case "arrow": {
            if (p.length < 4) break;
            const [x1, y1, x2, y2] = [p[0]!, p[1]!, p[2]!, p[3]!];
            ctx.strokeStyle = inkFor(o.color, dark);
            ctx.lineWidth = o.size;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            if (o.kind === "arrow" && (x1 !== x2 || y1 !== y2)) {
                const angle = Math.atan2(y2 - y1, x2 - x1);
                const head = arrowHead(o.size);
                ctx.moveTo(x2, y2);
                ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7));
                ctx.moveTo(x2, y2);
                ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7));
            }
            ctx.stroke();
            break;
        }
        case "rect":
        case "ellipse": {
            if (p.length < 4) break;
            const r = normRect(p[0]!, p[1]!, p[2]!, p[3]!);
            ctx.strokeStyle = inkFor(o.color, dark);
            ctx.lineWidth = o.size;
            ctx.beginPath();
            if (o.kind === "rect") ctx.rect(r.x1, r.y1, r.x2 - r.x1, r.y2 - r.y1);
            else ctx.ellipse((r.x1 + r.x2) / 2, (r.y1 + r.y2) / 2, (r.x2 - r.x1) / 2, (r.y2 - r.y1) / 2, 0, 0, Math.PI * 2);
            ctx.stroke();
            break;
        }
        case "text": {
            ctx.fillStyle = inkFor(o.color, dark);
            ctx.font = fontFor(o.size);
            ctx.textBaseline = "top";
            const lines = textLines(o.text ?? "", o.size, o.w);
            lines.forEach((line, i) => ctx.fillText(line, o.x ?? 0, (o.y ?? 0) + i * o.size * LINE_HEIGHT + o.size * 0.1));
            break;
        }
        case "note": {
            const x = o.x ?? 0, y = o.y ?? 0, w = o.w ?? NOTE_W, h = o.h ?? NOTE_H;
            ctx.shadowColor = "rgba(0,0,0,0.18)";
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 2;
            ctx.fillStyle = o.color;
            ctx.fillRect(x, y, w, h);
            ctx.shadowColor = "transparent";
            ctx.save();
            ctx.beginPath();
            ctx.rect(x, y, w, h);
            ctx.clip();
            // Sticky notes are coloured paper: their text stays dark in both themes.
            ctx.fillStyle = INK;
            ctx.font = fontFor(o.size);
            ctx.textBaseline = "top";
            const pad = 10;
            const lines = textLines(o.text ?? "", o.size, w - pad * 2);
            lines.forEach((line, i) => ctx.fillText(line, x + pad, y + pad + i * o.size * LINE_HEIGHT));
            ctx.restore();
            break;
        }
    }
    ctx.restore();
}

/** Background, dot grid and every object inside the viewport. */
export function paintBoard(
    ctx: CanvasRenderingContext2D,
    objects: WbObject[],
    view: View,
    cssW: number,
    cssH: number,
    dpr: number,
    dark: boolean
) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = dark ? BOARD_BG.dark : BOARD_BG.light;
    ctx.fillRect(0, 0, cssW, cssH);

    // Dot grid every 32 world units (skipped when zoomed far out).
    const step = 32 * view.zoom;
    if (step >= 10) {
        ctx.fillStyle = dark ? GRID.dark : GRID.light;
        const ox = -((view.x * view.zoom) % step);
        const oy = -((view.y * view.zoom) % step);
        const r = Math.max(0.8, Math.min(1.6, view.zoom));
        for (let sx = ox; sx < cssW; sx += step) {
            for (let sy = oy; sy < cssH; sy += step) {
                ctx.fillRect(sx - r / 2, sy - r / 2, r, r);
            }
        }
    }

    ctx.setTransform(dpr * view.zoom, 0, 0, dpr * view.zoom, -view.x * view.zoom * dpr, -view.y * view.zoom * dpr);
    const viewport: Rect = { x1: view.x, y1: view.y, x2: view.x + cssW / view.zoom, y2: view.y + cssH / view.zoom };
    for (const o of objects) {
        if (intersects(bounds(o), viewport)) drawObject(ctx, o, dark);
    }
}

/** The whole board as a PNG on white (what "Save to Drive" and "Download" produce). */
export function renderBoardPng(objects: WbObject[], maxSide = 4096): Promise<Blob> {
    const b = contentBounds(objects) ?? { x1: 0, y1: 0, x2: 800, y2: 600 };
    const pad = 40;
    const w = b.x2 - b.x1 + pad * 2;
    const h = b.y2 - b.y1 + pad * 2;
    const scale = Math.min(2, maxSide / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(w * scale));
    canvas.height = Math.max(1, Math.ceil(h * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return Promise.reject(new Error("Couldn't render the whiteboard."));
    ctx.fillStyle = BOARD_BG.light;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, (pad - b.x1) * scale, (pad - b.y1) * scale);
    for (const o of objects) drawObject(ctx, o, false);
    return new Promise((resolve, reject) =>
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Couldn't render the whiteboard."))), "image/png"));
}

/** "<subject> — Whiteboard 2026-09-13 14-05.png" (nothing Windows or Drive would reject). */
export function boardFileName(subject: string, at = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}-${pad(at.getMinutes())}`;
    const safe = subject.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 120) || "Meeting";
    return `${safe} — Whiteboard ${stamp}.png`;
}

/**
 * Split ops into WhiteboardOps calls: ≤ 50 ops and ≤ MAX_OPS_JSON characters
 * each. An op too big on its own is dropped (and reported) rather than sent.
 */
export function chunkOps(ops: WbOp[]): { batches: string[]; dropped: number } {
    const batches: string[] = [];
    let dropped = 0;
    let current: string[] = [];
    let length = 2;
    for (const op of ops) {
        const json = JSON.stringify(op);
        if (json.length + 2 > MAX_OPS_JSON) {
            dropped++;
            continue;
        }
        if (current.length >= 50 || length + json.length + 1 > MAX_OPS_JSON) {
            batches.push(`[${current.join(",")}]`);
            current = [];
            length = 2;
        }
        current.push(json);
        length += json.length + 1;
    }
    if (current.length > 0) batches.push(`[${current.join(",")}]`);
    return { batches, dropped };
}
